import {
  getBlessingStyle,
  getMapStyle,
  getMuseumStyle,
  getRevealStyleClass,
} from './themeRevealRules';

export const firstTextValue = (...values) => (
  values
    .map((value) => (typeof value === 'string' ? value.trim() : ''))
    .find(Boolean)
);

const firstNumberValue = (...values) => (
  values
    .map((value) => Number(value))
    .find((value) => Number.isFinite(value) && value > 0)
);

export const truncateText = (value, maxLength = 72) => {
  const text = firstTextValue(value);
  if (!text) return '';
  return text.length > maxLength ? `${text.slice(0, maxLength - 1).trim()}...` : text;
};

export const getOpenWhenLetters = (module) => {
  const data = module?.data || {};
  const letters = data.letters || data.prompts || data.items;
  if (Array.isArray(letters)) return letters.filter(Boolean);

  const legacyLabel = firstTextValue(data.title, data.label, data.prompt);
  const legacyMessage = firstTextValue(data.message, data.content, data.hint, data.body);
  if (!legacyLabel && !legacyMessage) return [];

  return [{
    title: legacyLabel || 'Open When',
    label: legacyLabel || 'Open When',
    message: legacyMessage,
  }];
};

const formatDuration = (seconds) => {
  if (!seconds) return '';
  const totalSeconds = Math.max(1, Math.round(seconds));
  const minutes = Math.floor(totalSeconds / 60);
  const remainingSeconds = totalSeconds % 60;
  return `${minutes}:${String(remainingSeconds).padStart(2, '0')}`;
};

export const getVoiceState = (module) => {
  const data = module?.data || {};
  const audioUrl = firstTextValue(
    data.audioUrl,
    data.recordedUrl,
    data.voiceUrl,
    data.audioPath,
    data.audio?.url,
  );
  const durationSeconds = firstNumberValue(
    data.durationSeconds,
    data.audioDurationSeconds,
    data.audioDuration,
    data.duration,
    data.recordingTime,
    data.audio?.durationSeconds,
  );

  return {
    hasAudio: Boolean(audioUrl),
    durationSeconds: durationSeconds || 0,
    durationLabel: formatDuration(durationSeconds),
  };
};

export const getMediaUrl = (item) => {
  if (typeof item === 'string') return item.trim();
  if (!item || typeof item !== 'object') return '';
  return firstTextValue(
    item.originalUrl,
    item.fullUrl,
    item.downloadURL,
    item.downloadUrl,
    item.storageUrl,
    item.uploadedUrl,
    item.publicUrl,
    item.url,
    item.imageUrl,
    item.mediaUrl,
    item.coverUrl,
    item.photoUrl,
    item.fileUrl,
    item.src,
    item.previewUrl,
    item.thumbnailUrl,
    item.thumbUrl,
  );
};

const isVideoMedia = (item, data = {}) => {
  if (!item || typeof item !== 'object') return false;
  return item.mediaType === 'video' || item.type === 'video' || item.kind === 'video' || data.mediaType === 'video';
};

export const getPhotoState = (module) => {
  const data = module?.data || {};
  const mediaItems = [
    ...(Array.isArray(data.images) ? data.images : []),
    ...(Array.isArray(data.photos) ? data.photos : []),
    ...(Array.isArray(data.gallery) ? data.gallery : []),
    ...(Array.isArray(data.photoItems) ? data.photoItems : []),
    ...(Array.isArray(data.mediaItems) ? data.mediaItems : []),
    ...(Array.isArray(data.snapshots) ? data.snapshots : []),
    ...(Array.isArray(data.slides) ? data.slides : []),
    ...(Array.isArray(data.items) && ['primary_media', 'frame_gallery'].includes(module?.type) ? data.items : []),
  ];
  const directItems = [
    { url: data.originalUrl },
    { url: data.fullUrl },
    { url: data.downloadURL },
    { url: data.downloadUrl },
    { url: data.storageUrl },
    { url: data.uploadedUrl },
    { url: data.publicUrl },
    { url: data.beforeImage },
    { url: data.beforeImageUrl },
    { url: data.afterImage },
    { url: data.afterImageUrl },
    { url: data.imageUrl },
    { url: data.photoUrl },
    { url: data.coverUrl },
    { url: data.posterUrl },
    { url: data.mediaType === 'video' ? firstTextValue(data.thumbnailUrl, data.videoThumbnailUrl, data.posterUrl, data.coverUrl) : data.mediaUrl },
  ];
  const videoUrl = firstTextValue(
    data.mediaType === 'video' ? data.mediaUrl : '',
    data.videoUrl,
    data.url && module?.type === 'video_player' ? data.url : '',
  );
  const seenUrls = new Set();
  const cards = [...mediaItems, ...directItems]
    .map((item) => ({
      url: getMediaUrl(item),
      isVideo: isVideoMedia(item, data),
    }))
    .filter((item) => item.url)
    .filter((item) => {
      if (seenUrls.has(item.url)) return false;
      seenUrls.add(item.url);
      return true;
    })
    .slice(0, 3);

  if (!cards.length && videoUrl) {
    cards.push({ url: firstTextValue(data.thumbnailUrl, data.videoThumbnailUrl, data.posterUrl, data.coverUrl), isVideo: true });
  }

  return {
    cards,
    hasMedia: cards.some((item) => item.url),
    hasVideo: Boolean(videoUrl) || cards.some((item) => item.isVideo),
    count: Math.max(cards.length, videoUrl ? 1 : 0),
  };
};

export const getThenNowState = (module) => {
  const data = module?.data || {};
  const beforeUrl = firstTextValue(data.beforeImage, data.beforeImageUrl, data.thenImage, data.thenImageUrl);
  const afterUrl = firstTextValue(data.afterImage, data.afterImageUrl, data.nowImage, data.nowImageUrl);

  return {
    beforeUrl,
    afterUrl,
    hasBefore: Boolean(beforeUrl),
    hasAfter: Boolean(afterUrl),
    hasBoth: Boolean(beforeUrl && afterUrl),
    beforeLabel: firstTextValue(data.beforeLabel, data.thenLabel, 'Then'),
    afterLabel: firstTextValue(data.afterLabel, data.nowLabel, 'Now'),
  };
};

export const getFrameGalleryState = (module) => {
  const data = module?.data || {};
  const imageUrl = firstTextValue(
    data.imageUrl,
    data.photoUrl,
    data.coverUrl,
    data.mediaUrl,
    Array.isArray(data.items) ? getMediaUrl(data.items[0]) : '',
  );

  return {
    imageUrl,
    hasImage: Boolean(imageUrl),
    frameStyle: firstTextValue(data.frameStyle, 'paper').toLowerCase().replace(/[^a-z0-9_-]/g, ''),
    caption: truncateText(firstTextValue(data.caption, data.title, data.label, 'A framed memory'), 34),
  };
};

const formatTimelineDate = (value, fallback) => {
  const rawValue = firstTextValue(value);
  if (!rawValue) return fallback;
  const isoMatch = rawValue.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (isoMatch) {
    const month = Number(isoMatch[2]);
    const day = Number(isoMatch[3]);
    const monthLabels = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${monthLabels[Math.max(0, Math.min(11, month - 1))]} ${day}`;
  }
  if (/^\d{4}$/.test(rawValue)) return rawValue;
  return rawValue.length > 9 ? rawValue.slice(0, 9) : rawValue;
};

export const getTimelineState = (module) => {
  const data = module?.data || {};
  const sourceItems = [
    ...(Array.isArray(data.timelineItems) ? data.timelineItems : []),
    ...(Array.isArray(data.items) ? data.items : []),
    ...(Array.isArray(data.stops) ? data.stops : []),
    ...(Array.isArray(data.milestones) ? data.milestones : []),
    ...(Array.isArray(data.events) ? data.events : []),
  ];
  const normalizedItems = sourceItems
    .filter(Boolean)
    .map((item, index, items) => {
      const dateValue = firstTextValue(item.dateLabel, item.date, item.year, item.month);
      const important = Boolean(
        item.important ||
        item.isImportant ||
        item.featured ||
        item.isFeatured ||
        item.highlight ||
        item.keyMoment ||
        index === 0 ||
        index === items.length - 1,
      );
      return {
        label: firstTextValue(item.label, item.title, item.place, item.name, `Moment ${index + 1}`),
        dateLabel: formatTimelineDate(dateValue, `Step ${index + 1}`),
        important,
      };
    });

  const visibleItems = normalizedItems.length ? normalizedItems.slice(0, 5) : [
    { label: 'Begin', dateLabel: 'Start', important: true },
    { label: 'Middle', dateLabel: 'Then', important: false },
    { label: 'Now', dateLabel: 'Now', important: true },
  ];

  return {
    count: normalizedItems.length,
    visibleItems,
    hasItems: normalizedItems.length > 0,
  };
};

export const getMapState = (module, context = {}) => {
  const data = module?.data || {};
  const sourceStops = [
    ...(Array.isArray(data.stops) ? data.stops : []),
    ...(Array.isArray(data.timelineItems) ? data.timelineItems : []),
    ...(Array.isArray(data.items) ? data.items : []),
    ...(Array.isArray(data.locations) ? data.locations : []),
    ...(Array.isArray(data.places) ? data.places : []),
  ];
  const normalizedStops = sourceStops
    .filter(Boolean)
    .map((stop, index) => ({
      label: firstTextValue(stop.place, stop.label, stop.title, stop.name, `Stop ${index + 1}`),
      dateLabel: formatTimelineDate(firstTextValue(stop.date, stop.dateLabel, stop.year), `Stop ${index + 1}`),
    }));
  const visibleStops = normalizedStops.length ? normalizedStops.slice(0, 5) : [
    { label: 'First Stop', dateLabel: 'Start' },
    { label: 'Memory', dateLabel: 'Then' },
    { label: 'Today', dateLabel: 'Now' },
  ];

  return {
    count: normalizedStops.length,
    hasStops: normalizedStops.length > 0,
    style: getMapStyle(context.themeId, context.revealStyle),
    revealStyleClass: getRevealStyleClass(context.revealStyle),
    pins: visibleStops,
  };
};

const getExhibitType = (item = {}) => {
  const explicitType = firstTextValue(item.type, item.kind, item.category, item.exhibitType).toLowerCase();
  if (['ticket', 'photo', 'note', 'date'].includes(explicitType)) return explicitType;
  if (firstTextValue(item.imageUrl, item.url, item.mediaUrl, item.thumbnailUrl)) return 'photo';
  if (firstTextValue(item.date, item.dateLabel, item.year)) return 'date';
  if (firstTextValue(item.note, item.message, item.backContent, item.description)) return 'note';
  return 'ticket';
};

export const getMuseumState = (module, context = {}) => {
  const data = module?.data || {};
  const sourceExhibits = [
    ...(Array.isArray(data.exhibits) ? data.exhibits : []),
    ...(Array.isArray(data.items) ? data.items : []),
    ...(Array.isArray(data.artifacts) ? data.artifacts : []),
    ...(Array.isArray(data.memories) ? data.memories : []),
  ];
  const normalizedExhibits = sourceExhibits
    .filter(Boolean)
    .map((item, index) => ({
      label: firstTextValue(item.title, item.label, item.name, `Exhibit ${index + 1}`),
      dateLabel: formatTimelineDate(firstTextValue(item.date, item.dateLabel, item.year), ''),
      type: getExhibitType(item),
    }));
  const visibleExhibits = normalizedExhibits.length ? normalizedExhibits.slice(0, 5) : [
    { label: 'Photo', dateLabel: '', type: 'photo' },
    { label: 'Note', dateLabel: '', type: 'note' },
    { label: 'Date', dateLabel: 'Today', type: 'date' },
  ];

  return {
    count: normalizedExhibits.length,
    hasExhibits: normalizedExhibits.length > 0,
    style: getMuseumStyle(context.themeId, context.revealStyle),
    revealStyleClass: getRevealStyleClass(context.revealStyle),
    exhibits: visibleExhibits,
  };
};

const getPolaroidCaption = (item = {}, data = {}) => truncateText(
  typeof item === 'object' ? firstTextValue(
    item.caption,
    item.message,
    item.note,
    item.title,
    item.name,
  ) : '',
  34,
) || truncateText(firstTextValue(data.caption, data.emptyMessage, data.prompt), 34);

export const getPolaroidState = (module) => {
  const data = module?.data || {};
  const sourcePhotos = [
    ...(Array.isArray(data.photos) ? data.photos : []),
    ...(Array.isArray(data.snapshots) ? data.snapshots : []),
    ...(Array.isArray(data.polaroids) ? data.polaroids : []),
    ...(Array.isArray(data.images) ? data.images : []),
    ...(Array.isArray(data.gallery) ? data.gallery : []),
    ...(Array.isArray(data.mediaItems) ? data.mediaItems : []),
    { url: data.coverUrl, caption: data.caption || data.title },
    { url: data.imageUrl, caption: data.caption || data.title },
    { url: data.photoUrl, caption: data.caption || data.title },
  ];
  const normalizedPhotos = sourcePhotos
    .map((item) => ({
      url: getMediaUrl(item),
      caption: getPolaroidCaption(item, data),
    }))
    .filter((item) => item.url);
  const firstPhoto = normalizedPhotos[0];

  return {
    hasPhoto: Boolean(firstPhoto?.url),
    photoUrl: firstPhoto?.url || '',
    caption: firstPhoto?.caption || 'Add a snapshot',
    count: normalizedPhotos.length,
  };
};

export const getBlessingState = (module, context = {}) => {
  const data = module?.data || {};
  const sourceMessages = [
    ...(Array.isArray(data.messages) ? data.messages : []),
    ...(Array.isArray(data.blessings) ? data.blessings : []),
    ...(Array.isArray(data.notes) ? data.notes : []),
    ...(Array.isArray(data.guestMessages) ? data.guestMessages : []),
    ...(Array.isArray(module?.messages) ? module.messages : []),
  ];
  const normalizedMessages = sourceMessages
    .map((message, index) => {
      const status = firstTextValue(message.status, message.moderationStatus, message.state);
      const isApproved = status === 'approved' || status === 'visible';
      const isBlocked = status === 'blocked' || status === 'hidden' || status === 'rejected';
      return {
        id: message.id || `blessing_${index}`,
        name: firstTextValue(message.name, message.author, message.from, 'Guest'),
        text: truncateText(message.message || message.text || message.note || message.body || message.content),
        approved: isApproved,
        visible: Boolean(!isBlocked && (isApproved || !status)),
      };
    })
    .filter((message) => message.visible && message.text);

  const visibleMessages = normalizedMessages.length ? normalizedMessages.slice(0, 3) : [
    {
      id: 'waiting_for_blessings',
      name: 'Guestbook',
      text: 'Waiting for blessings',
      approved: false,
      placeholder: true,
    },
  ];

  return {
    count: normalizedMessages.length,
    hasMessages: normalizedMessages.length > 0,
    style: getBlessingStyle(context.themeId, context.revealStyle),
    revealStyleClass: getRevealStyleClass(context.revealStyle),
    messages: visibleMessages,
  };
};
